import { Building2, Package, MapPin, Calendar, FileText, Link as LinkIcon, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import type { ComplaintWithRelations } from '@/core/api';
import { ComplaintStatusBadge } from './ComplaintStatusBadge';
import { Card, CardContent, CardHeader } from '@/shared/ui/card';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { useToast } from '@/shared/hooks/use-toast';

interface ComplaintCardProps {
  complaint: ComplaintWithRelations;
  onStatusChange?: (complaintId: string, newStatus: string) => void;
  onEdit?: (complaintId: string) => void;
}

/**
 * Individual complaint card in the list view.
 */
export const ComplaintCard = ({ complaint, onStatusChange, onEdit }: ComplaintCardProps) => {
  const { toast } = useToast();

  const handleLink8D = () => {
    toast({
      title: 'Coming Soon',
      description: 'Linking complaints to 8D reports will be available in a future release.',
    });
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 space-y-2">
            {/* Customer Name */}
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <h3 className="text-lg font-semibold">
                {complaint.customer?.name || 'Unknown Customer'}
              </h3>
              {complaint.customer?.internal_code && (
                <Badge variant="outline" className="text-xs">
                  {complaint.customer.internal_code}
                </Badge>
              )}
            </div>

            {/* Complaint Identifier */}
            {complaint.complaint_identifier && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span>Ref: {complaint.complaint_identifier}</span>
              </div>
            )}
          </div>

          <div className="flex flex-col items-end gap-2">
            <ComplaintStatusBadge status={complaint.status} />
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {/* Date */}
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Date:</span>
            <span className="font-medium">
              {format(new Date(complaint.complaint_date), 'MMM dd, yyyy')}
            </span>
          </div>

          {/* Article */}
          {complaint.article && (
            <div className="flex items-center gap-2">
              <Package className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Article:</span>
              <span className="font-medium">
                {complaint.article.code}
                {complaint.article.name && ` - ${complaint.article.name}`}
              </span>
            </div>
          )}

          {/* Location */}
          {(complaint.area || complaint.line) && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">Location:</span>
              <span className="font-medium">
                {complaint.area?.name}
                {complaint.line && ` / ${complaint.line.name}`}
              </span>
            </div>
          )}

          {/* Serial Number */}
          {complaint.serial_number && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Serial:</span>
              <span className="font-mono font-medium text-xs">
                {complaint.serial_number}
              </span>
            </div>
          )}
        </div>

        {/* Comment */}
        {complaint.comment && (
          <div className="p-3 bg-muted/50 rounded-md">
            <p className="text-sm text-muted-foreground line-clamp-2">{complaint.comment}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={() => onEdit?.(complaint.id)}>
            <Pencil className="h-4 w-4 mr-2" />
            View / Edit
          </Button>

          {complaint.status === 'complaint_recorded' && !complaint.eight_d_claim_id && (
            <Button variant="outline" size="sm" onClick={handleLink8D}>
              <LinkIcon className="h-4 w-4 mr-2" />
              Link to 8D
            </Button>
          )}

          {complaint.created_by_profile && (
            <span className="text-xs text-muted-foreground ml-auto">
              Entered by {complaint.created_by_profile.full_name || complaint.created_by_profile.email}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
